// Hover panel for the selection sphere. DOM-only driver registered as a
// listener for 'ui:hoverTooltip:{show,hide,reflow}' on the store.
//
// Shows X/Y/Z + field magnitude plus two action buttons (Trace, Info).
// Dispatched by picking/interactions during hover/drag. Interactive
// (pointer-events:auto) so the buttons are clickable/tappable.
//
// The skeleton (rows + buttons + listeners) is built ONCE — the pinned path
// re-calls showValuesAt every frame, so we only update value text, never rebuild
// markup (which would thrash the DOM and drop button focus). That per-frame
// re-assert also keeps a pinned panel open: it cancels any pending hide each
// frame, so a pointerleave can't commit while pinned.
//
// Theme colors come from CSS custom properties on :root.

import { INTENTS } from '@store/intents';
import { openProbeInfo } from './selections/infoWindow';
import type { Store } from '@store/store';
import type { State } from '@store/schema';

const HIDE_TRANSITION_MS = 160;
// Bridges the few px of bare canvas between the marker's hover zone and the
// panel, so moving the cursor onto a button doesn't make the panel vanish first.
const HIDE_GRACE_MS = 150;

interface HoverSample {
    posPhys: { x: number; y: number; z: number };
    value: number;
    fieldLabel: string;
    coordUnits: string;
    fieldUnits: string;
}

function shortUnit(u: string | undefined, threshold: number, keep: number): string {
    const s = String(u || '');
    if (s.length <= threshold) return s;
    return s.slice(0, keep) + '..';
}

interface HoverTooltipHandle {
    showValuesAt: (screenX: number, screenY: number, sample: HoverSample, pinned: boolean) => void;
    hideValues: () => void;
    reflow: (screenX: number, screenY: number) => void;
    dispose: () => void;
}

interface ValueRow {
    label: HTMLSpanElement;
    val: HTMLElement;
}

/** Append one label→value pair to the two parallel columns (labels left,
 *  values right) and return the cached refs for per-frame text updates. */
function makeRow(
    labelText: string,
    fieldRow: boolean,
    labelsCol: HTMLElement,
    valsCol: HTMLElement,
): ValueRow {
    const label = document.createElement('span');
    label.className = 'magviz-hover-tooltip__label';
    label.textContent = labelText;
    const val = fieldRow ? document.createElement('strong') : document.createElement('span');
    const valWrap = document.createElement('span');
    valWrap.className = 'magviz-hover-tooltip__val' + (fieldRow ? ' magviz-hover-tooltip__field-val' : '');
    valWrap.appendChild(val);
    labelsCol.appendChild(label);
    valsCol.appendChild(valWrap);
    return { label, val };
}

/** Build a rail-styled action button (38×38, FA icon). */
function makeButton(label: string, iconClass: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'magviz-hover-tooltip__btn';
    btn.setAttribute('aria-label', label);
    btn.title = label;
    const icon = document.createElement('i');
    icon.className = 'fas ' + iconClass;
    icon.setAttribute('aria-hidden', 'true');
    btn.appendChild(icon);
    btn.addEventListener('click', (ev) => {
        // Keep the click off the canvas: the document-capture pointerdown in
        // picking/interactions already early-returns for non-canvas targets, but
        // stop propagation defensively so no body-level handler reacts.
        ev.preventDefault();
        ev.stopPropagation();
        onClick();
    });
    return btn;
}

function createHoverTooltip(store: Store<State>): HoverTooltipHandle {
    let el: HTMLDivElement | null = null;
    let valuesEl: HTMLDivElement | null = null;
    let rowX: ValueRow | null = null;
    let rowY: ValueRow | null = null;
    let rowZ: ValueRow | null = null;
    let rowField: ValueRow | null = null;

    let graceTimer: ReturnType<typeof setTimeout> | null = null;
    let fadeTimer: ReturnType<typeof setTimeout> | null = null;
    let valuesShown = false;
    let pointerOver = false;

    function traceFromSelection(): void {
        store.dispatch(INTENTS.TRACE_FIELDLINE);
    }

    function ensure(): HTMLDivElement {
        if (el) return el;
        el = document.createElement('div');
        el.className = 'magviz-hover-tooltip';
        el.setAttribute('data-mode', 'values');

        valuesEl = document.createElement('div');
        valuesEl.className = 'magviz-hover-tooltip__values';
        // Two parallel columns: labels (left, on the darker fill) and values
        // (right). Kept separate so the fill can bleed to the card border.
        const labelsCol = document.createElement('div');
        labelsCol.className = 'magviz-hover-tooltip__labels';
        const valsCol = document.createElement('div');
        valsCol.className = 'magviz-hover-tooltip__vals';
        rowX = makeRow('X', false, labelsCol, valsCol);
        rowY = makeRow('Y', false, labelsCol, valsCol);
        rowZ = makeRow('Z', false, labelsCol, valsCol);
        rowField = makeRow('Field', true, labelsCol, valsCol);

        const actions = document.createElement('div');
        actions.className = 'magviz-hover-tooltip__actions';
        actions.appendChild(makeButton('Trace field line', 'fa-bezier-curve', traceFromSelection));
        // Sits below Trace (actions column); opens a floating window with the
        // full field detail at the probed point.
        actions.appendChild(makeButton('Field info', 'fa-info-circle', () => openProbeInfo()));
        valuesEl.appendChild(labelsCol);
        valuesEl.appendChild(valsCol);
        valuesEl.appendChild(actions);

        el.appendChild(valuesEl);

        // Keep-alive: while the pointer is over the panel it must not hide. The
        // per-frame showValuesAt re-assert cancels any scheduled hide when pinned,
        // so these handlers only need to drive the unpinned hover case.
        el.addEventListener('pointerenter', () => { pointerOver = true; cancelHide(); });
        el.addEventListener('pointerleave', () => { pointerOver = false; scheduleHide(HIDE_GRACE_MS); });

        // The panel needs pointer-events:auto for its buttons, but that also
        // swallows wheel scrolls so OrbitControls (bound to the canvas) never
        // zooms when the cursor is over the panel. Re-dispatch the wheel onto the
        // canvas so zoom works anywhere in the scene, panel or not.
        el.addEventListener('wheel', (ev: WheelEvent) => {
            const canvas = document.querySelector<HTMLCanvasElement>('#3d-canvas canvas');
            if (!canvas) return;
            ev.preventDefault();
            canvas.dispatchEvent(new WheelEvent('wheel', {
                deltaX: ev.deltaX, deltaY: ev.deltaY, deltaZ: ev.deltaZ,
                deltaMode: ev.deltaMode, clientX: ev.clientX, clientY: ev.clientY,
                bubbles: false, cancelable: true,
            }));
        }, { passive: false });

        document.body.appendChild(el);
        return el;
    }

    function place(tip: HTMLDivElement, screenX: number, screenY: number): void {
        const offsetX = 17, offsetY = 17;
        const height = tip.offsetHeight || 40;
        tip.style.left = Math.round(screenX + offsetX) + 'px';
        tip.style.top = Math.round(screenY - offsetY - height) + 'px';
    }

    function positionAndShow(tip: HTMLDivElement, screenX: number, screenY: number): void {
        tip.style.display = 'block';
        place(tip, screenX, screenY);
        cancelHide();
        requestAnimationFrame(() => { if (el) el.setAttribute('data-visible', '1'); });
    }

    /** Move an already-visible panel to follow the re-projected marker as the
     *  camera orbits. Pure reposition — does not cancel a pending hide or touch
     *  data-visible, so a transient/fading panel keeps its lifecycle. */
    function reflow(screenX: number, screenY: number): void {
        if (!el || el.style.display === 'none') return;
        place(el, screenX, screenY);
    }

    function renderValues(sample: HoverSample): void {
        if (!el || !rowX || !rowY || !rowZ || !rowField) return;
        el.setAttribute('data-mode', 'values');
        const { posPhys, value, fieldLabel, coordUnits, fieldUnits } = sample;
        const cu = shortUnit(coordUnits, 6, 4);
        const fu = shortUnit(fieldUnits, 10, 10);
        const csuf = cu ? (' ' + cu) : '';
        rowX.val.textContent = (isFinite(posPhys.x) ? posPhys.x.toFixed(2) : '—') + csuf;
        rowY.val.textContent = (isFinite(posPhys.y) ? posPhys.y.toFixed(2) : '—') + csuf;
        rowZ.val.textContent = (isFinite(posPhys.z) ? posPhys.z.toFixed(2) : '—') + csuf;
        rowField.label.textContent = fieldLabel || 'Field';
        rowField.val.textContent = (isFinite(value) ? value.toFixed(3) : '—') + (fu ? (' ' + fu) : '');
    }

    function showValuesAt(screenX: number, screenY: number, sample: HoverSample, pinned: boolean): void {
        const tip = ensure();
        valuesShown = true;
        // data-pinned drives the full-opacity CSS state; otherwise the panel
        // sits a bit see-through unless the cursor is directly on it (:hover).
        if (pinned) tip.setAttribute('data-pinned', '1');
        else tip.removeAttribute('data-pinned');
        renderValues(sample);
        positionAndShow(tip, screenX, screenY);
    }

    function hideValues(): void {
        valuesShown = false;
        if (pointerOver) return; // pointer is on the panel; pointerleave will hide
        scheduleHide(HIDE_GRACE_MS);
    }

    function scheduleHide(delay: number): void {
        cancelHide();
        graceTimer = setTimeout(() => { graceTimer = null; commitFade(); }, delay);
    }

    function commitFade(): void {
        if (!el) return;
        if (fadeTimer) { clearTimeout(fadeTimer); fadeTimer = null; }
        el.removeAttribute('data-visible');
        fadeTimer = setTimeout(() => { if (el) el.style.display = 'none'; }, HIDE_TRANSITION_MS);
    }

    function cancelHide(): void {
        if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }
        if (fadeTimer) { clearTimeout(fadeTimer); fadeTimer = null; }
    }

    function dispose(): void {
        cancelHide();
        if (el && el.parentNode) el.parentNode.removeChild(el);
        el = null;
        valuesEl = null;
        rowX = rowY = rowZ = rowField = null;
        valuesShown = false;
        pointerOver = false;
    }

    return { showValuesAt, hideValues, reflow, dispose };
}

export function installHoverTooltipDriver(store: Store<State>): () => void {
    const tip = createHoverTooltip(store);
    const offs = [
        store.onIntent(INTENTS.UI_HOVER_SHOW, (p) => {
            if (!p) return;
            tip.showValuesAt(p.screenX, p.screenY, p.sample, p.pinned === true);
        }),
        store.onIntent(INTENTS.UI_HOVER_HIDE, () => tip.hideValues()),
        store.onIntent(INTENTS.UI_HOVER_REFLOW, (p) => {
            if (p) tip.reflow(p.screenX, p.screenY);
        }),
    ];
    return () => { offs.forEach((off) => off()); tip.dispose(); };
}
